import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Plus, MapPin, Calendar, Layers, Loader2, ArrowRight, Crown } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { fileUrl } from "@/components/ImageUpload";
import { STATUS_META, fmtDate, categoryLabel, canCreateTournaments, subscriptionStatus } from "@/lib/tournament";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span data-testid={`tournament-status-${status}`} className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0 flex items-center gap-1" style={{ color: m.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} /> {m.l}
    </span>
  );
}

export default function Tournaments() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreate = canCreateTournaments(user);
  const subExpired = subscriptionStatus(user).state === "expired";
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get("/tournaments").then(({ data }) => setList(data || [])).catch(() => setList(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Tournament Suite" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grain">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Module 2 · Pro</span>
            <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2">Tournament Suite</h1>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Create sanctioned brackets, manage rosters and run credentialed umpire scoring.</p>
          </div>
          {canCreate ? (
            <button data-testid="new-tournament-button" onClick={() => nav("/tournaments/new")}
              className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Tournament
            </button>
          ) : (
            <button data-testid="upgrade-cta" onClick={() => nav("/subscribe")}
              className="glow-border rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
              <Crown className="w-4 h-4 text-primary-c" /> Subscribe to unlock
            </button>
          )}
        </motion.div>

        <SubscriptionBanner className="mt-6" />

        {!canCreate && !subExpired && (
          <div className="mt-6 rounded-2xl glow-border p-6 flex items-start gap-4" style={{ background: "var(--surface)" }} data-testid="upgrade-banner">
            <div className="w-11 h-11 rounded-xl glass flex items-center justify-center shrink-0"><Crown className="w-5 h-5 text-primary-c" /></div>
            <div className="flex-1">
              <h3 className="font-display font-bold">Tournament Suite is a paid feature</h3>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Subscribe to create sanctioned brackets, online registration and umpire scoring. Pay via QR, upload your receipt, and an admin will activate your account.</p>
              <button data-testid="banner-subscribe" onClick={() => nav("/subscribe")}
                className="btn-glow rounded-full px-5 py-2.5 mt-4 text-sm font-bold inline-flex items-center gap-2">
                <Crown className="w-4 h-4" /> Subscribe now
              </button>
            </div>
          </div>
        )}

        {list === null && <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>}
        {list === false && <p className="mt-10 text-sm" style={{ color: "var(--text-2)" }}>Could not load tournaments.</p>}

        {Array.isArray(list) && list.length === 0 && (
          <div className="mt-10 rounded-3xl glow-border p-12 text-center" style={{ background: "var(--surface)" }} data-testid="tournaments-empty">
            <Trophy className="w-10 h-10 mx-auto text-primary-c opacity-70" />
            <h3 className="font-display font-bold text-xl mt-4">No tournaments yet</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>{canCreate ? "Launch your first event with the setup wizard." : "Subscribe to launch your first event."}</p>
            {canCreate && (
              <button data-testid="empty-new-tournament" onClick={() => nav("/tournaments/new")}
                className="btn-glow rounded-full px-6 py-3 mt-6 font-bold inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create tournament
              </button>
            )}
          </div>
        )}

        {Array.isArray(list) && list.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="tournaments-list">
            {list.map((t, i) => (
              <motion.button key={t.tournament_id} data-testid={`tournament-card-${t.tournament_id}`} onClick={() => nav(`/tournaments/${t.tournament_id}`)}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}
                className="group text-left rounded-2xl glow-border overflow-hidden hover:scale-[1.01] transition-transform" style={{ background: "var(--surface)" }}>
                <div className="relative aspect-[3/1] overflow-hidden" style={{ background: "var(--bg)" }}>
                  {t.banner_url ? (
                    <img src={fileUrl(t.banner_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Trophy className="w-8 h-8 text-primary-c opacity-30" /></div>
                  )}
                  <div className="absolute top-2 right-2"><StatusBadge status={t.status} /></div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold truncate">{t.name}</h3>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{ color: "var(--text-2)" }}>
                    {t.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.venue}</span>}
                    {t.start_date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmtDate(t.start_date)}</span>}
                    <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{(t.categories || []).length} categories</span>
                  </div>
                  {(t.categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {t.categories.slice(0, 3).map((c) => (
                        <span key={c.id} className="text-[10px] glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }}>{categoryLabel(c)}</span>
                      ))}
                      {t.categories.length > 3 && <span className="text-[10px] glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }}>+{t.categories.length - 3}</span>}
                    </div>
                  )}
                  <span className="mt-4 inline-flex items-center gap-2 font-bold text-primary-c text-sm group-hover:gap-3 transition-all">Manage <ArrowRight className="w-4 h-4" /></span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
